import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { RELOCATION_FAQ_CATEGORIES } from "@/content/relocation-corpus/faq-defs";

const ADVICE_EN = [
  "Legalise home-country documents before you fly — marriage certificate, birth certificates, transcripts and police clearance — because obtaining them retroactively from Thailand takes far longer.",
  "State in the lease that the housemaster or landlord must file TM30 within 24 hours and hand the acknowledgement to the tenant for visa filings.",
  "If you plan to stay beyond three years, register the lease at the Land Office under section 538 rather than relying on a one-year auto-renewing contract.",
  "Obtain a residence certificate from your local immigration office early: it is the starting document for a driving licence, vehicle registration and many bank accounts.",
  "State the purpose of every inward remittance correctly on the day of transfer if you intend to buy in the condominium foreign quota.",
  "Check the current minimum insurance sums for the visa you are filing (Non-O-A or LTR) in the notification in force, not figures you heard earlier.",
  "Verify the school is registered with the Office of the Private Education Commission and issues transcripts your next destination will accept.",
  "Sequence the work: visa, then accommodation and TM30, then bank account and residence certificate, then driving licence and school places.",
];

const CAUTIONS_EN = [
  "Assuming TM30 is the tenant's job — section 38 places the duty on the housemaster, owner or possessor of the dwelling.",
  "Filing the 90-day report late, when it can be filed from 15 days before the due date to 7 days after, and the count restarts on each re-entry.",
  "Signing a lease with someone who is neither the owner nor an authorised attorney, leaving you unable to assert the lease against the real owner.",
  "Skipping a condition report and inventory at move-in, which makes recovering the deposit hard once damage is alleged.",
  "Believing a yellow house book or pink card ends 90-day reporting — that duty attaches to temporary-stay permission.",
  "Driving long-term on an International Driving Permit after settling in Thailand instead of obtaining a Thai licence, which insurers may question.",
  "Drafting your own bank power of attorney; most Thai banks accept only their own form, notarised and legalised.",
  "Submitting a health policy that does not show cover inside Thailand or lacks a translation, which sends you back to redo documents.",
];

export const Route = createFileRoute("/en/relocation/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/relocation-corpus");
    return { faqs: m.RELOCATION_FAQ_EN };
  },
  component: EnRelocationFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Relocating to Thailand — ${faqs.length} FAQs`,
        description:
          "Leases and lease registration, TM30 notification and 90-day reporting, opening a bank account, visa health insurance, schools, Thai driving licences, the yellow house book and pink card, and accompanied government visits.",
        path: "/en/relocation/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/relocation/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Relocation FAQ", url: `${site}/en/relocation/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/relocation/faq")) },
      ],
    };
  },
});

function EnRelocationFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Relocation FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Relocating to Thailand: Housing, TM30, Banking, Insurance, Schools, Driving and Registration
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers grounded in the Immigration Act B.E. 2522 (sections 37(4) and 38), the Civil and
          Commercial Code on hire of property, Department of Land Transport rules, the civil registration law, and the
          published insurance conditions for Non-O-A and LTR visas.
        </p>

        <section aria-label="Topics" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {RELOCATION_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/relocation/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Let our team sequence your move first</h2>
          <p className="text-sm text-muted-foreground">
            Tell us your nationality, current visa, family size and destination province — by phone, LINE or email,
            Monday to Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="relocation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}